import { useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { usePortalChat, type FailedSend } from "../components/context";
import { ATTACHMENTS_MAX_COUNT, MESSAGE_TEXT_MAX_LENGTH, TYPING_EMIT_INTERVAL_MS } from "../portalChat.constants";
import type { ChatMember } from "../api/portalChat.api";

/**
 * The minimal shape a platform file must expose. Web passes DOM `File`s (name/type/size are own
 * properties); mobile passes expo picker descriptors ({ uri, name, type, size }).
 */
export type ComposerFile = { name: string; type?: string; size?: number };

export type PendingAttachment<TFile extends ComposerFile> = {
  id: string;
  file: TFile;
  /** `| undefined` as well as optional: the upload machine writes the temp key back onto the row
   *  and stores `undefined` for an upload that resolved without one. */
  key?: string | undefined;
  status: "uploading" | "done" | "error";
};

export type { FailedSend };

/**
 * The composer state machine, shared by the web thread pane and the mobile thread screen: draft text
 * (clamped to the zod cap), the pending-attachment upload machine (PRD §4.4 — a message posts only
 * once ALL attachments succeed; failed uploads are retried or removed first), @-mention tracking
 * (only mentions whose name survives in the text are submitted), the typing throttle, and the
 * failed-send bubble bound to ITS conversation.
 *
 * The uploader is injected because transports differ: web posts DOM File/Blob parts, mobile RN
 * `{uri,name,type}` descriptors — both to /fileSystemServer/uploadFileInTemp, both resolving to a
 * temp key or null.
 */
export function useChatComposer<TFile extends ComposerFile>({
  conversationId,
  memberCount,
  upload,
  onSendStarted,
}: {
  /** The OPEN conversation — the composer resets whenever it changes. */
  conversationId: string | null;
  /** Mention picker only offers itself in threads with more than one member. */
  memberCount: number;
  upload: (file: TFile) => Promise<string | null>;
  /** Fired as a send begins (web re-arms stick-to-bottom before the append lands). */
  onSendStarted?: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();

  const [text, setText] = useState("");
  const [pending, setPending] = useState<PendingAttachment<TFile>[]>([]);
  const [mentions, setMentions] = useState<Pick<ChatMember, "_id" | "fullName">[]>([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [sending, setSending] = useState(false);
  // The failed-send bubble lives in the CORE so it survives a mobile screen unmount and keeps
  // its binding to ITS conversation; this hook only reads/writes it.
  const { failedSend, setFailedSend } = chat;

  const typingSentAtRef = useRef(0);
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  // Reset only when a DIFFERENT conversation appears. A transient deselect (mobile camera flow
  // pushes the ImageEditor screen, blurring and deselecting the thread) keeps the draft and the
  // already-uploaded attachments intact for the return trip, so a null id never latches.
  const [lastConversation, setLastConversation] = useState<string | null>(conversationId);
  if (conversationId && !Object.is(lastConversation, conversationId)) {
    setLastConversation(conversationId);
    setText("");
    setPending([]);
    setMentions([]);
    setShowMentionPicker(false);
  }
  // BUG (unfixed, pre-existing): the reset above does not clear `sending` or `typingSentAtRef`.
  // Switching threads mid-send leaves the new thread's send button disabled until the previous
  // request resolves, and suppresses its first `emitTyping` for up to TYPING_EMIT_INTERVAL_MS.
  // `sending` cannot simply be cleared here — see the conversation-scoping bug in `handleSend`.
  // BUG (unfixed, pre-existing, needs ChatThreadScreen.tsx): mobile passes null until
  // `chat.selectedId` catches up with the route, so the first non-null id latches and wipes
  // anything typed in that window. The fix is to gate the composer on selection having landed.

  const runUpload = async (item: PendingAttachment<TFile>) => {
    try {
      const key = await uploadRef.current(item.file);
      setPending((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, key: key ?? undefined, status: key ? "done" : "error" } : p)),
      );
    } catch {
      setPending((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "error" } : p)));
    }
  };

  const addFiles = async (files: TFile[]) => {
    if (!files.length) return;
    // Mirrors the zod cap: a message carries at most ATTACHMENTS_MAX_COUNT attachments.
    const room = ATTACHMENTS_MAX_COUNT - pending.length;
    if (room <= 0) {
      chat.showToast(t("A message can carry at most {{count}} attachments.", { count: ATTACHMENTS_MAX_COUNT }), false);
      return;
    }
    if (files.length > room) {
      chat.showToast(t("A message can carry at most {{count}} attachments.", { count: ATTACHMENTS_MAX_COUNT }), false);
    }
    const items: PendingAttachment<TFile>[] = files.slice(0, room).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: "uploading" as const,
    }));
    setPending((prev) => [...prev, ...items]);
    await Promise.all(items.map(runUpload));
  };

  const retryUpload = async (item: PendingAttachment<TFile>) => {
    setPending((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "uploading" } : p)));
    await runUpload(item);
  };

  const removePending = (id: string) => setPending((prev) => prev.filter((p) => p.id !== id));
  /** "Remove all" on the staged-attachments bar. */
  const removeAllPending = () => setPending([]);

  const uploading = pending.some((p) => p.status === "uploading");
  const failed = pending.some((p) => p.status === "error");

  const resetComposer = () => {
    setText("");
    setPending([]);
    setMentions([]);
    chat.setComposerIntent(null);
  };

  const handleSend = async () => {
    if (!conversationId) return;
    const trimmed = text.trim();
    // PRD §4.4: the message posts only once ALL attachments succeed — a failed upload must be
    // retried or removed first.
    if (sending || uploading || failed) return;
    const readyAttachments = pending.flatMap((p) =>
      p.status === "done" && p.key
        ? // `FailedSend["attachments"]` declares `type` / `size` as `?: T` without `undefined`, so
          // each key is omitted rather than written as an explicit `undefined` for a file that has
          // no mime type or size. The send payload is JSON, where both spellings are identical.
          [
            {
              key: p.key,
              name: p.file.name,
              ...(p.file.type ? { type: p.file.type } : {}),
              ...(p.file.size !== undefined && { size: p.file.size }),
            },
          ]
        : [],
    );
    if (!trimmed && !readyAttachments.length) return;
    setSending(true);
    onSendStarted?.();
    // Only mentions whose inserted @Name is still present in the text count (PRD: mentions are
    // what the sender actually left in the message).
    const liveMentions = mentions.filter((m) => trimmed.includes(m.fullName)).map((m) => m._id);
    const attempt: FailedSend = {
      conversationId,
      text: trimmed,
      attachments: readyAttachments,
      // Same note as the attachments above: `FailedSend.replyToMessageId` is `?: string` without
      // `undefined`, so the key is left off entirely when this send is not a reply.
      ...(chat.composerIntent?.kind === "reply" && { replyToMessageId: chat.composerIntent.message._id }),
      mentions: liveMentions,
    };
    // BUG (unfixed, pre-existing): `resetComposer()` below is NOT scoped to the conversation this
    // send was captured for, unlike the `failedSend` clear. Switching threads while the request is
    // in flight makes the resolution wipe the NEW thread's draft, attachments and reply intent.
    // Same class in `retryFailedSend`, which resends `failedSend` whatever thread is now open.
    const ok = await chat.sendMessage(attempt);
    setSending(false);
    if (ok) {
      // The slot is core-global: only clear a bubble that belongs to THIS conversation — a
      // success in chat B must not silently discard chat A's unsent text.
      if (failedSend?.conversationId === conversationId) setFailedSend(null);
      resetComposer();
    } else {
      // PRD §4.16 "Message failed to send (online)": an inline failed bubble with Retry; the
      // composer is freed for the next message.
      setFailedSend(attempt);
      resetComposer();
    }
  };

  const retryFailedSend = async () => {
    if (!failedSend || sending) return;
    setSending(true);
    const ok = await chat.sendMessage(failedSend);
    setSending(false);
    if (ok) setFailedSend(null);
  };

  const handleTextChange = (value: string) => {
    setText(value.slice(0, MESSAGE_TEXT_MAX_LENGTH));
    if (value.endsWith("@") && memberCount > 1) setShowMentionPicker(true);
    else setShowMentionPicker(false);
    if (!conversationId) return;
    const nowMs = Date.now();
    if (nowMs - typingSentAtRef.current > TYPING_EMIT_INTERVAL_MS) {
      typingSentAtRef.current = nowMs;
      chat.emitTyping(conversationId, true);
    }
  };

  /** Appends "Full Name " to the draft and registers the mention (deduped). */
  const insertMention = (member: Pick<ChatMember, "_id" | "fullName">) => {
    setShowMentionPicker(false);
    setText((prev) => `${prev}${member.fullName} `);
    setMentions((prev) => (prev.some((m) => m._id === member._id) ? prev : [...prev, member]));
  };

  /** Append a fragment (emoji picks) respecting the length cap. All-or-nothing: slicing a
   *  multi-unit emoji at the cap would strand a lone surrogate and store mojibake. */
  const insertText = (fragment: string) => {
    setText((prev) => (prev.length + fragment.length <= MESSAGE_TEXT_MAX_LENGTH ? `${prev}${fragment}` : prev));
  };

  const dismissFailedSend = () => setFailedSend(null);

  return {
    text,
    pending,
    mentions,
    showMentionPicker,
    setShowMentionPicker,
    sending,
    uploading,
    failed,
    failedSend,
    addFiles,
    retryUpload,
    removePending,
    removeAllPending,
    handleSend,
    retryFailedSend,
    handleTextChange,
    insertMention,
    insertText,
    dismissFailedSend,
  };
}
